import { useState, useEffect } from "react";
import { SharedHeader, SharedFooter, PageHero, Breadcrumb } from "../../components/site/SharedLayout";
import { getPatientSupport } from "../../services/api";
import { API_BASE_URL } from "../../config";
import PageLoader from "../../components/site/PageLoader";
import SEO from "../../components/SEO";


const API_BASE = API_BASE_URL;
const C = {
  navy: "#1a3c6e",
  navyLight: "#e5ecf9",
  orange: "#B16116",
  orangeHover: "#d06b1a",
  orangeLight: "#fff3e0",
  bg: "#f5f7fb",
  white: "#ffffff",
};

const inputBase = {
  width: "100%",
  padding: "11px 14px",
  border: `2px solid ${C.navyLight}`,
  borderRadius: 8,
  fontSize: 14,
  fontFamily: "inherit",
  outline: "none",
  background: C.white,
  color: "#222",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontWeight: 600,
  color: C.navy,
  marginBottom: 6,
  fontSize: 13,
};

const sectionHeaderStyle = {
  background: C.navy,
  color: C.white,
  padding: "10px 18px",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.06em",
  marginBottom: 20,
  marginTop: 28,
};

const fieldGroupStyle = {
  display: "grid",
  gap: 20,
  marginBottom: 20,
};

/* ──────────────────────── reusable field components ───────────────────────── */
const Field = ({ label, required, children, style }) => (
  <div style={style}>
    <label style={labelStyle}>
      {label} {required && <span style={{ color: C.orange }}>*</span>}
    </label>
    {children}
  </div>
);

const TextInput = ({ name, value, onChange, placeholder, type = "text", disabled }) => (
  <input
    type={type}
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    style={{ ...inputBase, background: disabled ? "#f0f4fa" : C.white, color: disabled ? "#999" : "#222" }}
    onFocus={e => !disabled && (e.target.style.borderColor = C.navy)}
    onBlur={e => (e.target.style.borderColor = C.navyLight)}
  />
);

const SelectInput = ({ name, value, onChange, children }) => (
  <select
    name={name}
    value={value}
    onChange={onChange}
    style={{ ...inputBase, appearance: "none", cursor: "pointer" }}
    onFocus={e => (e.target.style.borderColor = C.navy)}
    onBlur={e => (e.target.style.borderColor = C.navyLight)}
  >
    {children}
  </select>
);

const TextArea = ({ name, value, onChange, placeholder, rows = 5 }) => (
  <textarea
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    rows={rows}
    style={{ ...inputBase, resize: "vertical" }}
    onFocus={e => (e.target.style.borderColor = C.navy)}
    onBlur={e => (e.target.style.borderColor = C.navyLight)}
  />
);

/* ────────────────────────── Privacy / Consent box ─────────────────────────── */
const PrivacyConsent = ({ checked, onChange, label, title, description }) => (
  <div style={{
    background: C.orangeLight,
    border: `1px solid #f5c99a`,
    borderRadius: 10,
    padding: "16px 20px",
    marginBottom: 24,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
      <span style={{ fontWeight: 700, color: C.navy, fontSize: 14 }}>{title}</span>
    </div>
    <div
      style={{ fontSize: 13, color: "#444", marginBottom: 12, lineHeight: 1.6 }}
      dangerouslySetInnerHTML={{ __html: description }}
    />
    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ marginTop: 2, accentColor: C.orange, width: 16, height: 16, flexShrink: 0 }}
      />
      <span style={{ fontSize: 13, color: "#222", fontWeight: 500, lineHeight: 1.5 }}>
        {label}
      </span>
    </label>
  </div>
);

/* ══════════════════════════ ADR FORM ══════════════════════════════════════════ */
const ADRForm = ({ content }) => {
  const adr = content?.adr || {};
  const settings = content?.settings || {};

  const [data, setData] = useState({
    consent: false,
    reporterType: [],
    reporterName: "",
    reporterCountry: "",
    wantsContact: "",
    mobile: "",
    email: "",
    patientName: "",
    gender: "",
    dob: "",
    age: "",
    brandName: "",
    genericName: "",
    batchLot: "",
    expiryDate: "",
    durationFrom: "",
    durationTo: "",
    eventOnset: "",
    eventStop: "",
    country: "",
    eventDescription: "",
    outcome: "",
  });

  const set = (field) => (e) =>
    setData(prev => ({ ...prev, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const toggleReporterType = (val) =>
    setData(prev => ({
      ...prev,
      reporterType: prev.reporterType.includes(val)
        ? prev.reporterType.filter(v => v !== val)
        : [...prev.reporterType, val],
    }));

  const contactEnabled = data.wantsContact === "yes";

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API_BASE}/submit-adr`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const CheckBox = ({ label, value }) => (
    <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 14, color: "#333" }}>
      <input
        type="checkbox"
        checked={data.reporterType.includes(value)}
        onChange={() => toggleReporterType(value)}
        style={{ accentColor: C.navy, width: 15, height: 15 }}
      />
      {label}
    </label>
  );

  const Radio = ({ label, value, field }) => (
    <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 14, color: "#333" }}>
      <input
        type="radio"
        name={field}
        value={value}
        checked={data[field] === value}
        onChange={set(field)}
        style={{ accentColor: C.navy, width: 15, height: 15 }}
      />
      {label}
    </label>
  );

  if (submitted) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px", color: C.navy }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="2" style={{ marginBottom: 16 }}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <p style={{ fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-line" }}>{adr.success_message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <PrivacyConsent
        checked={data.consent}
        onChange={set("consent")}
        title={adr.privacy_title}
        description={adr.privacy_description}
        label={adr.consent_text}
      />

      <div style={sectionHeaderStyle}>{adr.reporter_heading}</div>

      <Field label={adr.reporter_type_label} style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <CheckBox label={adr.healthcare_professional_label} value="hcp" />
          <CheckBox label={adr.patient_label} value="patient" />
          <CheckBox label={adr.others_label} value="others" />
        </div>
      </Field>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={adr.name_label} required>
          <TextInput name="reporterName" value={data.reporterName} onChange={set("reporterName")} placeholder={adr.name_placeholder} />
        </Field>
        <Field label={adr.country_label} required>
          <TextInput name="reporterCountry" value={data.reporterCountry} onChange={set("reporterCountry")} placeholder={adr.country_placeholder} />
        </Field>
      </div>

      <Field label={adr.contact_permission_label} style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 24 }}>
          <Radio label={adr.yes_label} value="yes" field="wantsContact" />
          <Radio label={adr.no_label} value="no" field="wantsContact" />
        </div>
      </Field>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr", marginBottom: 8 }}>
        <Field label={adr.mobile_label}>
          <TextInput name="mobile" value={data.mobile} onChange={set("mobile")} placeholder={adr.mobile_placeholder} type="tel" disabled={!contactEnabled} />
        </Field>
        <Field label={adr.email_label}>
          <TextInput name="email" value={data.email} onChange={set("email")} placeholder={adr.email_placeholder} type="email" disabled={!contactEnabled} />
        </Field>
      </div>
      {!contactEnabled && (
        <p style={{ fontSize: 12, color: "#999", marginBottom: 20 }}>Select "Yes" above to enable contact fields.</p>
      )}

      <div style={sectionHeaderStyle}>{adr.patient_heading}</div>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={adr.patient_name_label} required>
          <TextInput name="patientName" value={data.patientName} onChange={set("patientName")} placeholder={adr.patient_name_placeholder} />
        </Field>
        <Field label={adr.gender_label} required>
          <SelectInput name="gender" value={data.gender} onChange={set("gender")}>
            <option value="">{adr.gender_placeholder}</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="unknown">Unknown</option>
          </SelectInput>
        </Field>
      </div>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={adr.dob_label}>
          <TextInput name="dob" value={data.dob} onChange={set("dob")} type="date" />
        </Field>
        <Field label={adr.age_label} required>
          <TextInput name="age" value={data.age} onChange={set("age")} placeholder={adr.age_placeholder} type="number" />
        </Field>
      </div>

      <div style={sectionHeaderStyle}>{adr.drug_heading}</div>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={adr.brand_name_label} required>
          <TextInput name="brandName" value={data.brandName} onChange={set("brandName")} placeholder={adr.brand_placeholder} />
        </Field>
        <Field label={adr.generic_name_label}>
          <TextInput name="genericName" value={data.genericName} onChange={set("genericName")} placeholder={adr.generic_placeholder} />
        </Field>
      </div>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={adr.batch_label} required>
          <TextInput name="batchLot" value={data.batchLot} onChange={set("batchLot")} placeholder={adr.batch_placeholder} />
        </Field>
        <Field label={adr.expiry_label} required>
          <TextInput name="expiryDate" value={data.expiryDate} onChange={set("expiryDate")} type="date" />
        </Field>
      </div>

      <Field label={adr.duration_label} style={{ marginBottom: 24 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "center" }}>
          <TextInput name="durationFrom" value={data.durationFrom} onChange={set("durationFrom")} placeholder={adr.duration_from_placeholder} type="date" />
          <span style={{ color: "#999", fontSize: 18, textAlign: "center" }}>→</span>
          <TextInput name="durationTo" value={data.durationTo} onChange={set("durationTo")} placeholder={adr.duration_to_placeholder} type="date" />
        </div>
      </Field>

      <div style={sectionHeaderStyle}>{adr.event_heading}</div>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={adr.event_onset_label}>
          <TextInput name="eventOnset" value={data.eventOnset} onChange={set("eventOnset")} type="date" />
        </Field>
        <Field label={adr.event_stop_label}>
          <TextInput name="eventStop" value={data.eventStop} onChange={set("eventStop")} type="date" />
        </Field>
      </div>

      <Field label={adr.event_country_label} style={{ marginBottom: 20 }}>
        <TextInput name="country" value={data.country} onChange={set("country")} placeholder={adr.event_country_placeholder} />
      </Field>

      <Field label={adr.event_description_label} required style={{ marginBottom: 20 }}>
        <TextArea
          name="eventDescription"
          value={data.eventDescription}
          onChange={set("eventDescription")}
          placeholder={adr.event_description_placeholder}
          rows={6}
        />
      </Field>

      <Field label={adr.outcome_label} style={{ marginBottom: 32 }}>
        <SelectInput name="outcome" value={data.outcome} onChange={set("outcome")}>
          <option value="">{adr.outcome_placeholder}</option>
          <option value="recovered">Recovered / Resolved</option>
          <option value="recovering">Recovering / Resolving</option>
          <option value="not_recovered">Not Recovered / Not Resolved</option>
          <option value="sequelae">Recovered with Sequelae</option>
          <option value="fatal">Fatal</option>
          <option value="unknown">Unknown</option>
        </SelectInput>
      </Field>

      <SubmitButton
        label={submitting ? "Submitting…" : adr.submit_button}
        mandatoryNote={settings.mandatory_note}
        disabled={submitting}
      />
      {submitError && (
        <p style={{ color: "#c0392b", fontSize: 13, textAlign: "center", marginTop: 10 }}>
          ⚠ {submitError}
        </p>
      )}
    </form>
  );
};

/* ══════════════════════ PRODUCT COMPLAINT FORM ════════════════════════════════ */
const ProductComplaintForm = ({ content }) => {
  const complaint = content?.complaint || {};
  const settings = content?.settings || {};

  const [data, setData] = useState({
    consent: false,
    complaintSource: "",
    responseLetterRequired: "",
    salutation: "",
    name: "",
    company: "",
    phone: "",
    email: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    additionalInfo: "",
    productName: "",
    complaintDescription: "",
    productDescription: "",
    batchNumber: "",
    expiryDate: "",
    files: [],
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (field) => (e) =>
    setData(prev => ({ ...prev, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleFiles = (e) => {
    setData(prev => ({ ...prev, files: Array.from(e.target.files) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    try {
      // Use FormData so uploaded files travel with the submission
      const form = new FormData();
      // Append all scalar fields
      const { files, ...fields } = data;
      Object.entries(fields).forEach(([k, v]) => form.append(k, v));
      // Append each file
      files.forEach((file) => form.append("files[]", file));

      const res = await fetch(`${API_BASE}/submit-complaint`, {
        method: "POST",
        body: form,
        // Do NOT set Content-Type — browser sets it with boundary automatically
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const CheckboxToggle = ({ label, value, field }) => (
    <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 14, color: "#333" }}>
      <input
        type="checkbox"
        value={value}
        checked={data[field] === value}
        onChange={() => setData(prev => ({ ...prev, [field]: prev[field] === value ? "" : value }))}
        style={{ accentColor: C.navy, width: 15, height: 15 }}
      />
      {label}
    </label>
  );

  if (submitted) {
    return (
      <div style={{
        textAlign: "center",
        padding: "40px 20px",
        color: C.navy,
      }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="2" style={{ marginBottom: 16 }}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <p style={{ fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-line" }}>{complaint.success_message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <PrivacyConsent
        checked={data.consent}
        onChange={set("consent")}
        title={complaint.privacy_title}
        description={complaint.privacy_description}
        label={complaint.consent_text}
      />

      <div style={sectionHeaderStyle}>{complaint.info_heading}</div>

      <Field label={complaint.source_label} required style={{ marginBottom: 20 }}>
        <SelectInput name="complaintSource" value={data.complaintSource} onChange={set("complaintSource")}>
          <option value="">{complaint.source_placeholder}</option>
          <option value="patient">Patient</option>
          <option value="hcp">Healthcare Professional</option>
          <option value="pharmacy">Pharmacy</option>
          <option value="distributor">Distributor</option>
          <option value="hospital">Hospital</option>
          <option value="other">Other</option>
        </SelectInput>
      </Field>

      <Field label={complaint.response_letter_label} required style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 24 }}>
          <CheckboxToggle label={complaint.yes_label} value="yes" field="responseLetterRequired" />
          <CheckboxToggle label={complaint.no_label} value="no" field="responseLetterRequired" />
        </div>
      </Field>

      <div style={sectionHeaderStyle}>{complaint.person_heading}</div>

      <Field label={complaint.salutation_label} required style={{ marginBottom: 20 }}>
        <SelectInput name="salutation" value={data.salutation} onChange={set("salutation")}>
          <option value="">{complaint.salutation_placeholder}</option>
          <option value="mr">Mr.</option>
          <option value="mrs">Mrs.</option>
          <option value="ms">Ms.</option>
          <option value="dr">Dr.</option>
          <option value="prof">Prof.</option>
        </SelectInput>
      </Field>

      <Field label={complaint.name_label} required style={{ marginBottom: 20 }}>
        <TextInput name="name" value={data.name} onChange={set("name")} placeholder={complaint.name_placeholder} />
      </Field>

      <Field label={complaint.company_label} style={{ marginBottom: 20 }}>
        <TextInput name="company" value={data.company} onChange={set("company")} placeholder={complaint.company_placeholder} />
      </Field>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={complaint.phone_label} required>
          <TextInput name="phone" value={data.phone} onChange={set("phone")} placeholder={complaint.phone_placeholder} type="tel" />
        </Field>
        <Field label={complaint.email_label} required>
          <TextInput name="email" value={data.email} onChange={set("email")} placeholder={complaint.email_placeholder} type="email" />
        </Field>
      </div>

      <Field label={complaint.street_label} required style={{ marginBottom: 20 }}>
        <TextInput name="street" value={data.street} onChange={set("street")} placeholder={complaint.street_placeholder} />
      </Field>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={complaint.city_label} required>
          <TextInput name="city" value={data.city} onChange={set("city")} placeholder={complaint.city_placeholder} />
        </Field>
        <Field label={complaint.state_label} required>
          <TextInput name="state" value={data.state} onChange={set("state")} placeholder={complaint.state_placeholder} />
        </Field>
      </div>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={complaint.zip_label} required>
          <TextInput name="zip" value={data.zip} onChange={set("zip")} placeholder={complaint.zip_placeholder} />
        </Field>
        <Field label={complaint.country_label} required>
          <TextInput name="country" value={data.country} onChange={set("country")} placeholder={complaint.country_placeholder} />
        </Field>
      </div>

      <Field label={complaint.additional_info_label} style={{ marginBottom: 24 }}>
        <TextArea name="additionalInfo" value={data.additionalInfo} onChange={set("additionalInfo")} placeholder={complaint.additional_info_placeholder} rows={3} />
      </Field>

      <div style={sectionHeaderStyle}>{complaint.product_heading}</div>

      <Field label={complaint.product_name_label} required style={{ marginBottom: 20 }}>
        <TextInput name="productName" value={data.productName} onChange={set("productName")} placeholder={complaint.product_name_placeholder} />
      </Field>

      <Field label={complaint.complaint_description_label} required style={{ marginBottom: 20 }}>
        <TextArea name="complaintDescription" value={data.complaintDescription} onChange={set("complaintDescription")} placeholder={complaint.complaint_description_placeholder} rows={5} />
      </Field>

      <Field label={complaint.product_description_label} style={{ marginBottom: 20 }}>
        <TextInput name="productDescription" value={data.productDescription} onChange={set("productDescription")} placeholder={complaint.product_description_placeholder} />
      </Field>

      <div style={{ ...fieldGroupStyle, gridTemplateColumns: "1fr 1fr" }}>
        <Field label={complaint.batch_number_label} required>
          <TextInput name="batchNumber" value={data.batchNumber} onChange={set("batchNumber")} placeholder={complaint.batch_number_placeholder} />
        </Field>
        <Field label={complaint.expiry_label} required>
          <TextInput name="expiryDate" value={data.expiryDate} onChange={set("expiryDate")} type="date" />
        </Field>
      </div>

      <Field label={complaint.upload_label} required style={{ marginBottom: 32 }}>
        <div
          style={{
            border: `2px dashed ${C.navyLight}`,
            borderRadius: 10,
            padding: "30px 20px",
            textAlign: "center",
            background: "#f8faff",
            cursor: "pointer",
          }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            setData(prev => ({ ...prev, files: Array.from(e.dataTransfer.files) }));
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.navy} strokeWidth="1.5" style={{ marginBottom: 10 }}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <p style={{ fontSize: 14, color: "#555", margin: "0 0 6px 0" }}>
            {complaint.upload_placeholder || "Drag & drop files here, or"}{" "}
            <label style={{ color: C.orange, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>
              {complaint.upload_browse_text || "browse"}
              <input type="file" multiple style={{ display: "none" }} onChange={handleFiles} accept="image/*,.pdf,.doc,.docx" />
            </label>
          </p>
          <p style={{ fontSize: 12, color: "#999", margin: 0 }}>{complaint.upload_allowed_types || "Images, PDFs, or documents (max 10 MB each)"}</p>
          {data.files.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {data.files.map((f, i) => (
                <div key={i} style={{ fontSize: 13, color: C.navy, fontWeight: 500 }}>📎 {f.name}</div>
              ))}
            </div>
          )}
        </div>
      </Field>

      <SubmitButton
        label={submitting ? "Submitting…" : complaint.submit_button}
        mandatoryNote={settings.mandatory_note}
        disabled={submitting}
      />
      {submitError && (
        <p style={{ color: "#c0392b", fontSize: 13, textAlign: "center", marginTop: 10 }}>
          ⚠ {submitError}
        </p>
      )}
    </form>
  );
};

/* ──────────────────────────── Submit Button ───────────────────────────────── */
const SubmitButton = ({ label, mandatoryNote, disabled }) => (
  <div style={{ marginBottom: 8 }}>
    <button
      type="submit"
      disabled={disabled}
      style={{
        width: "100%",
        background: disabled ? "#aaa" : C.orange,
        color: C.white,
        border: "none",
        borderRadius: 8,
        padding: "15px 24px",
        fontSize: 15,
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        letterSpacing: "0.03em",
        transition: "background 0.2s",
      }}
      onMouseEnter={e => { if (!disabled) e.target.style.background = C.orangeHover; }}
      onMouseLeave={e => { if (!disabled) e.target.style.background = C.orange; }}
    >
      {label}
    </button>
    {mandatoryNote && (
      <p style={{ textAlign: "center", fontSize: 12, color: "#999", marginTop: 10 }}>
        {mandatoryNote}
      </p>
    )}
  </div>
);

/* ────────────────── Tab Card ──────────────────────────────────────────────── */
const TabCard = ({ active, onClick, icon, title, desc }) => (
  <div
    onClick={onClick}
    style={{
      background: active ? C.navy : C.white,
      color: active ? C.white : C.navy,
      padding: "28px 28px 24px",
      borderRadius: 14,
      border: `2px solid ${active ? C.navy : C.navyLight}`,
      cursor: "pointer",
      transition: "all 0.25s ease",
      boxShadow: active ? "0 8px 28px rgba(26,60,110,0.22)" : "0 4px 14px rgba(0,0,0,0.05)",
      position: "relative",
    }}
  >
    {active && (
      <div style={{
        position: "absolute", top: 14, right: 14,
        width: 10, height: 10, borderRadius: "50%", background: C.orange,
      }} />
    )}
    <div style={{
      width: 44, height: 44, borderRadius: "50%",
      background: active ? "rgba(224,123,42,0.18)" : C.navyLight,
      display: "flex", alignItems: "center", justifyContent: "center",
      marginBottom: 16, color: active ? C.orange : C.navy,
    }}>
      {icon}
    </div>
    <h3 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 10px 0", lineHeight: 1.3 }}>{title}</h3>
    <p style={{ fontSize: 18, lineHeight: 1.65, margin: 0, opacity: active ? 0.8 : 0.6 }}>{desc}</p>
  </div>
);

/* ════════════════════════════ MAIN PAGE ════════════════════════════════════════ */
const PatientSupportPage = () => {
  const [activeTab, setActiveTab] = useState("adr");
  const [content, setContent] = useState(null);
  const seo = content?.seo || {};


  useEffect(() => {
    async function loadData() {
      const data = await getPatientSupport();
      setContent(data);
    }
    loadData();
  }, []);

  if (!content) {
    return <PageLoader />;
  }

  const hero = content?.hero || {};
  const adr = content?.adr || {};
  const complaint = content?.complaint || {};

  return (
    <>
  <SEO
    title={seo.title}
    description={seo.description}
    canonical={seo.canonical}
    robots={seo.robots}
    keywords={seo.keywords}
    ogTitle={seo.ogTitle}
    ogDescription={seo.ogDescription}
    ogImage={seo.ogImage}
    twitterTitle={seo.twitterTitle}
    twitterDescription={seo.twitterDescription}
    twitterImage={seo.twitterImage}
    author="GLS Pharma"
  publisher="GLS Pharma"
  />
    <div style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}>
      <SharedHeader />

      <Breadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: hero.breadcrumb_title || "Patient Support", path: "/patient-support" },        ]}
      />

      <PageHero
        label={hero.breadcrumb_title}
        title={hero.title}
        subtitle={hero.subtitle}
        bgImage={hero.banner?.url}
      />

      <section style={{ background: C.bg, padding: "60px 80px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>

          {/* Tab cards */}
          <div
  className="support-card-grid"
  style={{
    display: "grid",
    gap: 24,
    marginBottom: 40,
  }}
>
            <TabCard
              active={activeTab === "adr"}
              onClick={() => setActiveTab("adr")}
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              }
              title={adr.card_title}
              desc={adr.card_description}
            />
            <TabCard
              active={activeTab === "complaint"}
              onClick={() => setActiveTab("complaint")}
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <path d="M8 21h8M12 17v4" />
                </svg>
              }
              title={complaint.card_title}
              desc={complaint.card_description}
            />
          </div>

          {/* Form panel */}
          <div style={{
            background: C.white,
            borderRadius: 16,
            boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            border: `1px solid ${C.navyLight}`,
            overflow: "hidden",
          }}>
            {/* Banner */}
            <div style={{
              background: C.navy,
              padding: "14px 24px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: C.orange,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {activeTab === "adr"
                  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
                }
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.orange, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {activeTab === "adr" ? adr.banner_title : complaint.banner_title}
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
                  {activeTab === "adr" ? adr.banner_description : complaint.banner_description}
                </div>
              </div>
            </div>

            <div style={{ padding: "32px 36px" }}>
              {activeTab === "adr" ? <ADRForm content={content} /> : <ProductComplaintForm content={content} />}
            </div>
          </div>

        </div>
      </section>

      <SharedFooter />
    </div>
    </>
  );
};

export default PatientSupportPage;